import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import InputGroup from 'react-bootstrap/InputGroup';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Overlay from 'react-bootstrap/Overlay';
import Tooltip from 'react-bootstrap/Tooltip';
import { useEffect, useRef, useState } from 'react';
import { PostalCodeCheckModal } from '../../Modals/PostalCodeCheckModal';
import { AddressCheckModal } from '../../Modals/AddressCheckModal';
import axios from 'axios';
import { LoadingSpinner } from '../../LoadingSpinner';
import { useNotifications } from '../../../contexts/NotificationsContext';
import { militaryLocationsState, taxIdOptions } from '../../../utils/dropdownOptions';

export const RecipientInformation = ({ formik, countries, states, cities, handleCountryChange, handleStateChange, isReturnShipmentTab, tabKey }) => {
    const { addNotification } = useNotifications();
    const [show, setShow] = useState(false);
    const [showAddressModal, setShowAddressModal] = useState(false);
    const [postalCode, setPostalCode] = useState('');
    const [cityInitial, setCityInitial] = useState('');
    const [stateProvince, setStateProvince] = useState('');
    const [postalCheckLoading, setPostalCheckLoading] = useState(false);
    const [addressCheckLoading, setAddressCheckLoading] = useState(false);
    const [addressCheckModalData, setAddressCheckModalData] = useState({});
    const [showTooltip, setShowTooltip] = useState(false);
    const target = useRef(null);
    const values = isReturnShipmentTab ? formik.values.returnData : formik.values;
    const errors = isReturnShipmentTab ? formik.errors.returnData : formik.errors;
    const touched = isReturnShipmentTab ? formik.touched.returnData : formik.touched;

    useEffect(() => {
        if (showTooltip) setShowTooltip(false);
    }, [tabKey])

    const handleClose = () => setShow(false);
    const handleShow = () => setShow(true);
    const handleAddressModalShow = () => setShowAddressModal(true);
    const handleAddressModalClose = () => {
        setShowAddressModal(false);
        setAddressCheckModalData({});
    };

    const checkPostalCode = async () => {
        setPostalCheckLoading(true);
        const values = isReturnShipmentTab ? formik.values.returnData : formik.values;
        const url = `${process.env.REACT_APP_BASE_URL}/fedex/validate/postal`;
        const reqBody = {
            carrierCode: 'FDXG',
            countryCode: values.country,
            stateOrProvinceCode: values.stateProvince,
            postalCode: values.postalCode,
            shipDate: values.shipDate
        }

        const missingFields = [];
        Object.keys(reqBody).forEach((i) => {
            if (!reqBody[i]) {
                missingFields.push(i);
            }
        })
        if (missingFields.length > 0) {
            setPostalCheckLoading(false);
            return alert('The following fields must be entered in order to validate postal code: ' + missingFields.join(', '));
        } else {
            try {
                const response = await axios.post(url, reqBody);
                const data = response.data;
                setPostalCode(data.data.output.cleanedPostalCode);
                setCityInitial(data.data.output.cityFirstInitials);
                setStateProvince(data.data.output.stateOrProvinceCode);
                setPostalCheckLoading(false);
                handleShow();
            } catch (err) {
                setPostalCheckLoading(false);
                const errDetails = err.response.data.details.errors && err.response.data.details.errors.length > 0 ? err.response.data.details.errors[0].code : '';
                addNotification('Error validating postal code.', errDetails || err.message, 'danger');
                console.log('Error validating postal code: ' + err);
            }
        }
    }

    const checkAddress = async (secondaryTrigger) => {
        const values = isReturnShipmentTab ? formik.values.returnData : formik.values;
        if (!values.address1 || !values.city || !values.postalCode) {
            if (secondaryTrigger === true) return;
            return alert('The following fields are all required for this feature: address1, city, postalCode');
        }
        if (showTooltip) {
            setShowTooltip(false);
        }
        setAddressCheckLoading(true);
        const url = `${process.env.REACT_APP_BASE_URL}/fedex/validate/address`;
        const address = [values.address1];
        values.address2 && address.push(values.address2);
        values.address3 && address.push(values.address3);
        const reqBody = {
            streetLines: address,
            city: values.city,
            stateOrProvinceCode: values.stateProvince,
            postalCode: values.postalCode,
            countryCode: values.country
        }
        try {
            const response = await axios.post(url, reqBody);
            const data = response.data;
            const addressData = data.data.output.resolvedAddresses[0];
            const obj = {
                address1: addressData.streetLinesToken[0],
                address2: addressData.streetLinesToken[1],
                address3: addressData.streetLinesToken[2],
                city: addressData.city,
                stateProvince: addressData.stateOrProvinceCode,
                country: addressData.countryCode,
                postalCode: addressData.postalCode,
                classification: addressData.classification
            }
            setAddressCheckLoading(false);
            if (secondaryTrigger === true) {
                return obj;
            }
            setAddressCheckModalData(obj);
            handleAddressModalShow();
        } catch (err) {
            setAddressCheckLoading(false);
            const errDetails = err.response.data.details.errors && err.response.data.details.errors.length > 0 ? err.response.data.details.errors[0].code : '';
            addNotification('Error validating address.', errDetails || err.message, 'danger');
            console.log('Error validating address: ' + err);
        }
    }

    const trimPostalCode = (postalCode) => {
        const dashIndex = postalCode.indexOf('-');
        if (dashIndex === -1) return postalCode;
        const trimmedPostalCode = postalCode.slice(0, dashIndex);
        return trimmedPostalCode;
    }

    const handlePostalCodeBlur = async () => {
        const values = isReturnShipmentTab ? formik.values.returnData : formik.values;
        const addressData = await checkAddress(true);
        if (addressData) {
            const originalPostalCode = trimPostalCode(values.postalCode);
            const suggestedPostalCode = trimPostalCode(addressData.postalCode);
            if (suggestedPostalCode !== originalPostalCode) {
                setShowTooltip(true);
                // TODO need to make this work between both tabs
                // Currently if it shows in one then it will show in the other, with weird behavior
            }
        }
    }

    return (
        <>
            <div className='form-group'>
                {!isReturnShipmentTab &&
                    <Form.Check
                        inline
                        type='checkbox'
                        id='includeReturnLabel'
                        label='Include a return label'
                        checked={formik.values.includeReturnLabel}
                        {...formik.getFieldProps('includeReturnLabel')}
                    />}
                {!isReturnShipmentTab &&
                    <Form.Check
                        inline
                        type='checkbox'
                        id='requestPdfLabels'
                        label='Request labels in PDF'
                        checked={formik.values.requestPdfLabels}
                        {...formik.getFieldProps('requestPdfLabels')}
                    />}
            </div>
            <Form.Group className='form-group'>
                <Form.Label>Country</Form.Label>
                <Form.Select
                    id={isReturnShipmentTab ? 'returnData.country' : 'country'}
                    {...formik.getFieldProps(isReturnShipmentTab ? 'returnData.country' : 'country')}
                    onChange={(e) => handleCountryChange(e)}
                >
                    <option value=''>Select an option</option>
                    {
                        countries.map((c) => (
                            <option key={c.id} value={c.iso2}>{c.iso2} - {c.name}</option>
                        ))
                    }
                </Form.Select>
                {
                    errors?.country && touched?.country ? (
                        <div className='error'>{errors?.country}</div>
                    ) : null
                }
            </Form.Group>
            <Form.Group className='form-group'>
                <Form.Label>Contact name</Form.Label>
                <Form.Control
                    id={isReturnShipmentTab ? 'returnData.contactName' : 'contactName'}
                    type='text'
                    placeholder='Enter contact name'
                    {...formik.getFieldProps(isReturnShipmentTab ? 'returnData.contactName' : 'contactName')}
                />
            </Form.Group>
            <Form.Group className='form-group'>
                <Form.Label>Company name</Form.Label>
                <Form.Control
                    id={isReturnShipmentTab ? 'returnData.companyName' : 'companyName'}
                    type='text'
                    placeholder='Enter company name'
                    {...formik.getFieldProps(isReturnShipmentTab ? 'returnData.companyName' : 'companyName')}
                />
                {
                    errors?.companyName && touched?.companyName ? (
                        <div className='error'>{errors?.companyName}</div>
                    ) : null
                }
            </Form.Group>
            <Form.Group className='form-group'>
                <Form.Label>Address 1</Form.Label>
                <Form.Control
                    id={isReturnShipmentTab ? 'returnData.address1' : 'address1'}
                    type='text'
                    {...formik.getFieldProps(isReturnShipmentTab ? 'returnData.address1' : 'address1')}
                />
                {
                    errors?.address1 && touched?.address1 ? (
                        <div className='error'>{errors?.address1}</div>
                    ) : null
                }
            </Form.Group>
            <Form.Group className='form-group'>
                <Form.Label>Address 2</Form.Label>
                <Form.Control
                    id={isReturnShipmentTab ? 'returnData.address2' : 'address2'}
                    type='text'
                    {...formik.getFieldProps(isReturnShipmentTab ? 'returnData.address2' : 'address2')}
                />
            </Form.Group>
            {!isReturnShipmentTab && <Form.Group className='form-group'>
                <Form.Label>Address 3</Form.Label>
                <InputGroup>
                    <Form.Control
                        id='address3'
                        type='text'
                        {...formik.getFieldProps('address3')}
                    />
                    <InputGroup.Text>(Express only)</InputGroup.Text>
                </InputGroup>
            </Form.Group>}
            <Row className='form-group'>
                <Form.Group as={Col}>
                    <Form.Label>Postal code</Form.Label>
                    <InputGroup>
                        <Form.Control
                            id={isReturnShipmentTab ? 'returnData.postalCode' : 'postalCode'}
                            type='text'
                            {...formik.getFieldProps(isReturnShipmentTab ? 'returnData.postalCode' : 'postalCode')}
                            onBlur={handlePostalCodeBlur}
                        />
                        {!isReturnShipmentTab &&
                            <Button onClick={checkPostalCode} variant='secondary'>
                                {postalCheckLoading ? <LoadingSpinner /> : 'Search'}
                            </Button>}
                    </InputGroup>
                </Form.Group>
                <Form.Group as={Col}>
                    <Form.Label>State/Province</Form.Label>
                    <Form.Select
                        id={isReturnShipmentTab ? 'returnData.stateProvince' : 'stateProvince'}
                        {...formik.getFieldProps(isReturnShipmentTab ? 'returnData.stateProvince' : 'stateProvince')}
                        onChange={(e) => handleStateChange(e)}
                    >
                        <option value=''>Select an option</option>
                        {
                            values.country === 'US' && militaryLocationsState.map((l) => (
                                <option key={l.value} value={l.value}>{l.value} - {l.description}</option>
                            ))
                        }
                        {
                            states.map((s) => (
                                <option key={s.id} value={s.state_code}>{s.state_code} - {s.name}</option>
                            ))
                        }
                    </Form.Select>
                </Form.Group>
            </Row>
            <Form.Group className='form-group'>
                <Form.Label>City</Form.Label>
                <InputGroup>
                    <Form.Control
                        list={isReturnShipmentTab ? 'returnData.cities' : 'cities'}
                        id={isReturnShipmentTab ? 'returnData.city' : 'city'}
                        {...formik.getFieldProps(isReturnShipmentTab ? 'returnData.city' : 'city')}
                    />
                    <datalist
                        id={isReturnShipmentTab ? 'returnData.cities' : 'cities'}
                    >
                        {
                            cities.map((c) => (
                                <option key={c.id} value={c.name}>{c.name}</option>
                            ))
                        }
                    </datalist>
                    <Button onClick={checkAddress} variant='secondary' ref={target}>
                        {addressCheckLoading ? <LoadingSpinner /> : 'Check'}
                    </Button>
                    <Overlay target={target.current} show={showTooltip} placement='top' variant='danger'>
                        {(props) => (
                            <Tooltip className='warning-bg' {...props}>
                                Postal code mismatch - check address
                            </Tooltip>
                        )}
                    </Overlay>
                </InputGroup>
            </Form.Group>
            <Row className='form-group'>
                <Form.Group as={Col}>
                    <Form.Label>Telephone</Form.Label>
                    <Form.Control
                        id={isReturnShipmentTab ? 'returnData.telephone' : 'telephone'}
                        type='text'
                        {...formik.getFieldProps(isReturnShipmentTab ? 'returnData.telephone' : 'telephone')}
                    />
                    {
                        errors?.telephone && touched?.telephone ? (
                            <div className='error'>{errors?.telephone}</div>
                        ) : null
                    }
                </Form.Group>
                <Form.Group as={Col}>
                    <Form.Label>Ext</Form.Label>
                    <Form.Control
                        id={isReturnShipmentTab ? 'returnData.ext' : 'ext'}
                        type='text'
                        {...formik.getFieldProps(isReturnShipmentTab ? 'returnData.ext' : 'ext')}
                    />
                </Form.Group>
            </Row>
            {!isReturnShipmentTab &&
                <Row className='form-group'>
                    <Form.Group>
                        <Form.Label>Tax ID/EIN</Form.Label>
                        <InputGroup>
                            <Form.Select
                                id='taxIdType'
                                name='taxIdType'
                                {...formik.getFieldProps('taxIdType')}
                            >
                                {
                                    taxIdOptions.map((i) => (
                                        <option key={i} value={i}>{i}</option>
                                    ))
                                }
                            </Form.Select>
                            <Form.Control
                                id='taxId'
                                type='text'
                                {...formik.getFieldProps('taxId')}
                            />
                        </InputGroup>
                    </Form.Group>
                </Row>}
            <Form.Check
                type='checkbox'
                id={isReturnShipmentTab ? 'returnData.residentialAddress' : 'residentialAddress'}
                label='This is a residential address'
                checked={values.residentialAddress}
                {...formik.getFieldProps(isReturnShipmentTab ? 'returnData.residentialAddress' : 'residentialAddress')}
            />
            <PostalCodeCheckModal
                show={show}
                handleClose={handleClose}
                postalCode={postalCode}
                cityInitial={cityInitial}
                stateProvince={stateProvince}
            />
            <AddressCheckModal
                formik={formik}
                show={showAddressModal}
                handleClose={handleAddressModalClose}
                suggestedValues={addressCheckModalData}
                isReturnShipmentTab={isReturnShipmentTab}
            />
        </>
    )
}